import { create } from "zustand";
import { parseMyDayPlan } from "@/lib/parseMyDayPlan";

type Stage = "builder" | "paste" | "preview" | "errorNoEntries";

export type Tag = "add" | "keep" | "remove";

export interface PreviewRow {
  taskId: string;
  title: string;
  tag: Tag;
  reason: string;
  // For "remove" rows the user can opt to KEEP (don't actually remove); default false.
  keepDespiteRemove?: boolean;
}

interface OpenArgs {
  currentMyDayIds: string[];
}

interface MyDayPlanState {
  open: boolean;
  stage: Stage;
  strategy: string;
  prompt: string;
  taskIndex: Record<string, string>;
  tasksMeta: Record<string, { title: string; parentTitle: string | null }>;
  response: string;
  parseError: "no_markers" | null;
  preview: PreviewRow[];
  ignoredCount: number;
  currentMyDayIds: string[];
  loadingPrompt: boolean;

  openFor: (args: OpenArgs, initialStrategy: string) => void;
  close: () => void;
  loadPromptStart: () => void;
  loadPromptDone: (
    prompt: string,
    taskIndex: Record<string, string>,
    tasksMeta: Record<string, { title: string; parentTitle: string | null }>
  ) => void;
  goToPaste: () => void;
  backToBuilder: () => void;
  backToPaste: () => void;
  setResponse: (s: string) => void;
  parse: () => void;
  toggleKeepDespiteRemove: (taskId: string) => void;
  removeFromPlan: (taskId: string) => void;
  reorderPreview: (from: number, to: number) => void;
}

export const useMyDayPlanStore = create<MyDayPlanState>((set, get) => ({
  open: false,
  stage: "builder",
  strategy: "",
  prompt: "",
  taskIndex: {},
  tasksMeta: {},
  response: "",
  parseError: null,
  preview: [],
  ignoredCount: 0,
  currentMyDayIds: [],
  loadingPrompt: false,

  openFor: ({ currentMyDayIds }, initialStrategy) =>
    set({
      open: true,
      stage: "builder",
      strategy: initialStrategy ?? "",
      prompt: "",
      taskIndex: {},
      tasksMeta: {},
      response: "",
      parseError: null,
      preview: [],
      ignoredCount: 0,
      currentMyDayIds: [...currentMyDayIds],
      loadingPrompt: false,
    }),
  close: () => set({ open: false }),

  loadPromptStart: () => set({ loadingPrompt: true }),
  loadPromptDone: (prompt, taskIndex, tasksMeta) =>
    set({ loadingPrompt: false, prompt, taskIndex, tasksMeta }),

  goToPaste: () => set({ stage: "paste", response: "", parseError: null }),
  backToBuilder: () => set({ stage: "builder", parseError: null }),
  backToPaste: () => set({ stage: "paste", parseError: null }),
  setResponse: (s) => set({ response: s, parseError: null }),

  parse: () => {
    const { response, taskIndex, currentMyDayIds, tasksMeta } = get();
    const result = parseMyDayPlan(response);
    if (!result.ok) {
      set({ parseError: "no_markers" });
      return;
    }

    let ignored = 0;
    const orderedKept: PreviewRow[] = [];
    const seenIds = new Set<string>();
    const currentSet = new Set(currentMyDayIds);

    for (const entry of result.entries) {
      const taskId = taskIndex[String(entry.number)];
      if (!taskId) {
        ignored++;
        continue;
      }
      if (seenIds.has(taskId)) continue;
      seenIds.add(taskId);
      const meta = tasksMeta[taskId];
      orderedKept.push({
        taskId,
        title: meta?.title ?? "(без названия)",
        reason: entry.reason,
        tag: currentSet.has(taskId) ? "keep" : "add",
      });
    }

    // Add "remove" rows for current MyDay tasks not present in AI plan.
    for (const id of currentMyDayIds) {
      if (!seenIds.has(id)) {
        const meta = tasksMeta[id];
        orderedKept.push({
          taskId: id,
          title: meta?.title ?? "(без названия)",
          reason: "",
          tag: "remove",
          keepDespiteRemove: false,
        });
      }
    }

    if (orderedKept.length === 0) {
      set({ stage: "errorNoEntries", ignoredCount: ignored });
    } else {
      set({ stage: "preview", preview: orderedKept, ignoredCount: ignored });
    }
  },

  toggleKeepDespiteRemove: (taskId) =>
    set((st) => ({
      preview: st.preview.map((r) =>
        r.taskId === taskId && r.tag === "remove"
          ? { ...r, keepDespiteRemove: !r.keepDespiteRemove }
          : r
      ),
    })),

  removeFromPlan: (taskId) =>
    set((st) => ({
      preview: st.preview.filter((r) => r.taskId !== taskId),
    })),

  reorderPreview: (from, to) =>
    set((st) => {
      if (from === to) return {};
      const next = [...st.preview];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return { preview: next };
    }),
}));
